import type { FitmentFieldDefinition } from "../../app/models/FitmentCatalog";
import type { StorefrontResultsSettings } from "../../app/models/StorefrontResultsSettings";
import { normalizeFitmentKey } from "../../app/utils/fitmentFields";
import { getFieldSampleValues } from "../../app/utils/fitmentSampleValues";

export type PreviewValues = Record<string, string>;
export type PreviewScenario = "matches" | "empty" | "variants";
export type PreviewProduct = {
    id: string; title: string; price: string; image: "rotor" | "pads" | "air" | "oil";
    type: string; collectionIds: number[]; variants: string[];
};

export function sampleVehicles(fields: FitmentFieldDefinition[], catalogName: string): PreviewValues[] {
    const motorcycles = /motorcycle|motorbike/i.test(catalogName);
    const vehicles = motorcycles
        ? [["2024", "Honda", "CBR600RR", "ABS"], ["2023", "Honda", "CBR650R", "Standard"], ["2024", "Yamaha", "MT-07", "Standard"]]
        : [["2024", "Ford", "F-150", "XLT"], ["2023", "Ford", "Ranger", "XLT"], ["2024", "Toyota", "Tacoma", "TRD Sport"]];
    return vehicles.map(([year, make, model, submodel], index) => {
        const standard: PreviewValues = { year, make, model, submodel };
        return Object.fromEntries(fields.map(field => {
            const key = normalizeFitmentKey(field);
            const samples = getFieldSampleValues(field);
            if (/\bcc\b/i.test(field.label) || key === "engine_cc") return [key, (motorcycles ? ["600", "650", "689"] : ["3500", "2300", "2400"])[index]];
            return [key, standard[key] || samples[index % samples.length]];
        }));
    });
}

export function sampleOptions(fields: FitmentFieldDefinition[], rows: PreviewValues[], values: PreviewValues, fieldIndex: number) {
    const field = fields[fieldIndex];
    const key = normalizeFitmentKey(field);
    const options = rows.filter(row => fields.slice(0, fieldIndex).every(previous => {
        const previousKey = normalizeFitmentKey(previous);
        return !values[previousKey] || values[previousKey] === row[previousKey];
    })).map(row => row[key]);
    return sortOptions(field, options);
}

export function sortOptions(field: FitmentFieldDefinition, values: string[]) {
    const numeric = field.dataType === "Number" || field.dataType === "Decimal" || field.sortMode !== "Alphabetical";
    return Array.from(new Set(values)).sort((a, b) => (field.sortDirection === "Desc" ? -1 : 1) * a.localeCompare(b, "en", { numeric }));
}

export function sampleGroups(settings: StorefrontResultsSettings, scenario: PreviewScenario) {
    const types = settings.availableProductTypes.length ? settings.availableProductTypes : ["Brakes", "Filters"];
    const collections = settings.availableCollections.length ? settings.availableCollections : [
        { id: -1, title: "Braking", handle: "braking" }, { id: -2, title: "Maintenance", handle: "maintenance" },
    ];
    const products: PreviewProduct[] = [
        { id: "rotor", title: "Brake rotor", price: "$89.00", image: "rotor", type: types[0], collectionIds: [collections[0].id], variants: ["Front", "Rear"] },
        { id: "pads", title: "Brake pads", price: "$34.00", image: "pads", type: types[0], collectionIds: [collections[0].id], variants: ["Standard"] },
        { id: "air", title: "Air filter", price: "$24.00", image: "air", type: types[1 % types.length], collectionIds: [collections[1 % collections.length].id], variants: ["Standard"] },
        { id: "oil", title: "Oil filter", price: "$18.00", image: "oil", type: types[1 % types.length], collectionIds: [collections[1 % collections.length].id], variants: ["Standard"] },
    ];
    const selected = scenario === "empty" ? [] : scenario === "variants" ? products.slice(0, 1) : products;
    selected.sort((a, b) => {
        const mode = settings.uiSettings?.resultsSortBy;
        if (mode === "price-asc" || mode === "price-desc") return (mode === "price-desc" ? -1 : 1) * (Number(a.price.slice(1)) - Number(b.price.slice(1)));
        return (mode === "title-desc" ? -1 : 1) * a.title.localeCompare(b.title);
    });
    const included = <T,>(mode: string, filters: T[], value: T) => mode === "All" || (mode === "Include" ? filters.includes(value) : !filters.includes(value));
    if (settings.groupResultsBy === "ProductType") return types
        .filter(type => included(settings.productTypeFilterMode, settings.productTypeFilterValues, type))
        .map(type => ({ id: type, title: type, products: selected.filter(p => p.type === type) })).filter(group => group.products.length);
    if (settings.groupResultsBy === "Collection") return collections
        .filter(collection => included(settings.collectionFilterMode, settings.collectionFilterIds, collection.id))
        .map(collection => ({ id: String(collection.id), title: collection.title, products: selected.filter(p => p.collectionIds.includes(collection.id)) })).filter(group => group.products.length);
    if (settings.groupResultsBy === "CuratedGroups") return settings.curatedGroups
        .filter(group => group.include).slice().sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
        .map((group, index) => ({ id: `custom-${index}`, title: group.name, products: selected.filter(p => p.collectionIds.some(id => group.collectionIds.includes(id))) })).filter(group => group.products.length);
    return selected.length ? [{ id: "all", title: "Matching parts", products: selected }] : [];
}

export function storefrontUrl(shopDomain?: string) {
    if (!shopDomain) return undefined;
    try {
        const url = new URL(`https://${shopDomain.replace(/^https?:\/\//, "")}`);
        return url.username || url.password || url.pathname !== "/" || url.search || url.hash ? undefined : url.origin;
    } catch { return undefined; }
}
